import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '../../../common/Button';
import { Loader } from '../../App/components/Loader';
import { getCurrentClassroomId } from '../selectors/users';
import { getClassroom } from '../../../firebase/classrooms';
import { listProjects } from '../../../firebase/projects';
import {
  getPublication,
  getSketchP5Version,
  publishClassroom,
  refreshPublication,
  unpublishClassroom
} from '../../../firebase/publications';
import { arViewerBase, buildPublicationUrl } from '../../../utils/arViewerUrl';

/**
 * Publish the current classroom as ONE public webapp: a QR/URL that opens the AR viewer with a picker
 * over the sketches the instructor chose. Staff-only (rendered from the isStaff-gated Classroom menu).
 *
 * What is published is a FROZEN snapshot per sketch, so students editing afterwards can't change what
 * visitors see; Refresh re-snapshots the same selection. The published app is public and read-only —
 * it never shows a login — so the labels here are the only identity a visitor sees, and they default
 * to the student's username but are the instructor's to rewrite.
 */

// Shown when the checked sketches span more than one p5 version: p5 can't be hot-swapped in the
// viewer, so switching between them is the one case that restarts the AR camera.
const VERSION_WARNING =
  'These sketches use p5 {{versions}}. Switching between versions in the published app reloads ' +
  'the AR camera. To avoid that, set them to the same version in the editor and publish again.';

const SketchRow = ({
  project,
  selected,
  version,
  onToggle,
  onNameChange,
  onLabelChange
}) => {
  const { t } = useTranslation();
  const inputId = `publish-sketch-${project.id}`;

  return (
    <li className="publish-modal__sketch">
      {/* The checkbox's accessible name stays the ORIGINAL project name even after the instructor
          renames the entry, so its identity is stable. */}
      <input
        id={inputId}
        type="checkbox"
        aria-label={project.name}
        checked={!!selected}
        onChange={(e) => onToggle(project, e.target.checked)}
      />
      {!selected && (
        <label htmlFor={inputId} className="publish-modal__sketch-name">
          {project.name}
        </label>
      )}

      {project.visibility === 'Private' && (
        <span className="publish-modal__private">
          {t('PublishClassroomModal.PrivateNote', {
            defaultValue:
              'Private — publishing makes this sketch publicly viewable'
          })}
        </span>
      )}

      {selected && (
        <>
          {/* Editable display name for the publication. Stored on the entry — the underlying project
              is never renamed. Prefilled with the project name. */}
          <input
            type="text"
            aria-label={t('PublishClassroomModal.NameFor', {
              defaultValue: 'Published name for {{name}}',
              name: project.name
            })}
            value={selected.sketchName}
            onChange={(e) => onNameChange(project.id, e.target.value)}
          />
          <input
            type="text"
            aria-label={t('PublishClassroomModal.LabelFor', {
              defaultValue: 'Label for {{name}}',
              name: project.name
            })}
            value={selected.label}
            onChange={(e) => onLabelChange(project.id, e.target.value)}
          />
          {version && (
            <span className="publish-modal__version">{`p5 ${version}`}</span>
          )}
        </>
      )}
    </li>
  );
};

SketchRow.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    visibility: PropTypes.string
  }).isRequired,
  selected: PropTypes.shape({
    sketchName: PropTypes.string,
    label: PropTypes.string
  }),
  version: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
};

SketchRow.defaultProps = { selected: null, version: null };

/** One collapsible owner group (a student, or the instructor's own sketches). Loads on first open.
 *  `classroomId` scopes the list to sketches created in that classroom; '' lists everything, which
 *  is what the instructor's own "My sketches" group wants. */
const OwnerGroup = ({
  username,
  heading,
  classroomId,
  children: renderRows
}) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState(null); // null = never opened
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next || projects) return;
    setLoading(true);
    try {
      const res = classroomId
        ? await listProjects(username, true, { classroomId })
        : await listProjects(username, true);
      setProjects(res.projects || []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <li className="publish-modal__owner">
      <button type="button" onClick={toggle} aria-expanded={open}>
        {heading || username}
      </button>
      {open && loading && <Loader />}
      {open && projects && projects.length === 0 && (
        <p>
          {t('ClassroomMenu.NoSketches', { defaultValue: 'No sketches yet' })}
        </p>
      )}
      {open && projects && projects.length > 0 && (
        <ul>{projects.map((project) => renderRows(project, username))}</ul>
      )}
    </li>
  );
};

OwnerGroup.propTypes = {
  username: PropTypes.string.isRequired,
  heading: PropTypes.string,
  classroomId: PropTypes.string,
  children: PropTypes.func.isRequired
};

OwnerGroup.defaultProps = { heading: null, classroomId: '' };

const PublishClassroomModal = () => {
  const { t } = useTranslation();
  const currentClassroomId = useSelector(getCurrentClassroomId);
  const username = useSelector((state) => state.user.username);
  const instructorSettings = useSelector(
    (state) => state.preferences.arSettings || null
  );

  const [classroom, setClassroom] = useState(null); // null = loading, false = none
  const [publication, setPublication] = useState(null);
  const [title, setTitle] = useState('');
  // projectId -> { label, sketchName } (both instructor-editable; stored on the entry, never on the
  // project). AR settings are always inherited from the student's session — no per-entry override.
  const [selection, setSelection] = useState({});
  const [versions, setVersions] = useState({}); // projectId -> p5 version
  const [busy, setBusy] = useState('');
  const [errors, setErrors] = useState([]);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!currentClassroomId) {
      setClassroom(false);
      return () => {};
    }
    Promise.all([
      getClassroom(currentClassroomId),
      getPublication(currentClassroomId).catch(() => null)
    ])
      .then(([room, published]) => {
        if (cancelled) return;
        setClassroom(room || false);
        setPublication(published || null);
        setTitle((published && published.title) || (room && room.name) || '');
        if (published) {
          // Reload the published set as the current selection, so Publish means "these, updated".
          const restored = {};
          const restoredVersions = {};
          published.entries.forEach((entry) => {
            restored[entry.projectId] = {
              label: entry.label,
              sketchName: entry.sketchName
            };
            restoredVersions[entry.projectId] = entry.p5Version;
          });
          setSelection(restored);
          setVersions(restoredVersions);
        }
      })
      .catch(() => {
        if (!cancelled) setClassroom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentClassroomId]);

  function handleToggle(project, checked, ownerUsername) {
    setSelection((current) => {
      const next = { ...current };
      if (checked) {
        next[project.id] = {
          label: ownerUsername,
          sketchName: project.name
        };
      } else {
        delete next[project.id];
      }
      return next;
    });
    // The version drives the cross-version warning, and is only worth reading for sketches actually
    // going out. It's the same read publish will do, so it's cheap to be honest here.
    if (checked && !versions[project.id]) {
      getSketchP5Version(project.id)
        .then((version) =>
          setVersions((current) => ({ ...current, [project.id]: version }))
        )
        .catch(() => {});
    }
  }

  function handleNameChange(projectId, sketchName) {
    setSelection((current) => ({
      ...current,
      [projectId]: { ...current[projectId], sketchName }
    }));
  }

  function handleLabelChange(projectId, label) {
    setSelection((current) => ({
      ...current,
      [projectId]: { ...current[projectId], label }
    }));
  }

  function applyResult(result) {
    setPublication(result.publication);
    setErrors(result.errors || []);
  }

  const selectedIds = Object.keys(selection);

  async function handlePublish() {
    setBusy(
      t('PublishClassroomModal.Publishing', { defaultValue: 'Publishing…' })
    );
    setErrors([]);
    try {
      const result = await publishClassroom({
        classroomId: currentClassroomId,
        classroomName: classroom.name,
        title,
        selections: selectedIds.map((projectId) => ({
          projectId,
          label: selection[projectId].label,
          sketchName: selection[projectId].sketchName
        })),
        instructorSettings,
        onProgress: (done, total) =>
          setBusy(
            t('PublishClassroomModal.Progress', {
              defaultValue: 'Publishing {{done}} of {{total}}…',
              done,
              total
            })
          )
      });
      applyResult(result);
    } catch (e) {
      setErrors([{ projectId: '', sketchName: '', message: e.message }]);
    } finally {
      setBusy('');
    }
  }

  async function handleRefresh() {
    setBusy(
      t('PublishClassroomModal.Refreshing', { defaultValue: 'Refreshing…' })
    );
    setErrors([]);
    try {
      applyResult(
        await refreshPublication({
          classroomId: currentClassroomId,
          instructorSettings,
          onProgress: (done, total) =>
            setBusy(
              t('PublishClassroomModal.Progress', {
                defaultValue: 'Publishing {{done}} of {{total}}…',
                done,
                total
              })
            )
        })
      );
    } catch (e) {
      setErrors([{ projectId: '', sketchName: '', message: e.message }]);
    } finally {
      setBusy('');
    }
  }

  async function handleUnpublish() {
    setBusy(
      t('PublishClassroomModal.Unpublishing', { defaultValue: 'Unpublishing…' })
    );
    try {
      await unpublishClassroom(currentClassroomId);
      setPublication(null);
      setConfirmingUnpublish(false);
    } catch (e) {
      setErrors([{ projectId: '', sketchName: '', message: e.message }]);
    } finally {
      setBusy('');
    }
  }

  if (classroom === null) {
    return (
      <div className="classroom-modal" style={{ minHeight: '6rem' }}>
        <Loader />
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="classroom-modal">
        <p>
          {t('ClassroomMenu.NoCurrentClassroom', {
            defaultValue: 'You are not in a classroom yet.'
          })}
        </p>
      </div>
    );
  }

  const url = publication
    ? buildPublicationUrl(arViewerBase(), currentClassroomId)
    : '';
  // p5 can't be hot-swapped in the viewer, so a switch BETWEEN versions costs an AR restart. Warn
  // while it's still fixable (change a sketch's version in the editor, publish again).
  const selectedVersions = Array.from(
    new Set(selectedIds.map((id) => versions[id]).filter(Boolean))
  );

  const renderRow = (project, ownerUsername) => (
    <SketchRow
      key={project.id}
      project={project}
      selected={selection[project.id]}
      version={versions[project.id]}
      onToggle={(p, checked) => handleToggle(p, checked, ownerUsername)}
      onNameChange={handleNameChange}
      onLabelChange={handleLabelChange}
    />
  );

  return (
    <div className="classroom-modal publish-modal">
      <label htmlFor="publish-title">
        {t('PublishClassroomModal.Title', { defaultValue: 'App title' })}
      </label>
      <input
        id="publish-title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {publication && (
        <div className="publish-modal__result">
          {url ? (
            <>
              <QRCodeSVG value={url} size={180} includeMargin />
              <p className="publish-modal__url">{url}</p>
            </>
          ) : (
            <p>
              {t('PublishClassroomModal.NoViewerUrl', {
                defaultValue:
                  'Set AR_VIEWER_URL in the editor config to generate the link.'
              })}
            </p>
          )}
        </div>
      )}

      {selectedVersions.length > 1 && (
        <p role="status" className="publish-modal__warning">
          {t('PublishClassroomModal.VersionWarning', {
            defaultValue: VERSION_WARNING,
            versions: selectedVersions.join(' and ')
          })}
        </p>
      )}

      <ul className="publish-modal__owners">
        <OwnerGroup
          username={username}
          heading={t('PublishClassroomModal.MySketches', {
            defaultValue: 'My sketches'
          })}
        >
          {renderRow}
        </OwnerGroup>
        {(classroom.students || []).map((student) => (
          <OwnerGroup
            key={student.uid}
            username={student.username}
            classroomId={currentClassroomId}
          >
            {renderRow}
          </OwnerGroup>
        ))}
      </ul>

      {errors.length > 0 && (
        <ul className="publish-modal__errors">
          {errors.map((error) => (
            <li key={`${error.projectId}-${error.message}`}>
              {error.sketchName
                ? `${error.sketchName}: ${error.message}`
                : error.message}
            </li>
          ))}
        </ul>
      )}

      {busy && <p>{busy}</p>}

      {!publication && (
        <p>
          {t('PublishClassroomModal.NotPublished', {
            defaultValue: 'This classroom is not published yet.'
          })}
        </p>
      )}

      <div className="publish-modal__actions">
        <Button
          onClick={handlePublish}
          disabled={!!busy || selectedIds.length === 0}
        >
          {t('PublishClassroomModal.Publish', { defaultValue: 'Publish' })}
        </Button>
        {publication && (
          <>
            <Button onClick={handleRefresh} disabled={!!busy}>
              {t('PublishClassroomModal.Refresh', { defaultValue: 'Refresh' })}
            </Button>
            {confirmingUnpublish ? (
              <Button onClick={handleUnpublish} disabled={!!busy}>
                {t('PublishClassroomModal.ConfirmUnpublish', {
                  defaultValue: 'Yes, unpublish'
                })}
              </Button>
            ) : (
              <Button
                onClick={() => setConfirmingUnpublish(true)}
                disabled={!!busy}
              >
                {t('PublishClassroomModal.Unpublish', {
                  defaultValue: 'Unpublish'
                })}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublishClassroomModal;
